import Text from "common/Typography/Text.tsx";
import React from "react";
import styled, { useTheme } from "styled-components";
import {
  Bot,
  ChartCandlestick,
  ChartPie,
  History,
  LucideIcon,
  Radio,
} from "lucide-react";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import {
  Description,
  Title,
} from "features/settings/CancelSubscription/styles.tsx";
import {
  AdvancedTrading,
  AdvancedTradingDark,
  Backtesting,
  BacktestingDark,
  Bots,
  BotsDark,
  FeaturesMainIcon,
  PortfolioTracking,
  PortfolioTrackingDark,
  SignalFollowing,
  SignalFollowingDark,
} from "features/settings/CancelSubscription/icons.tsx";
import PageLayout from "features/settings/CancelSubscription/PageLayout.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

const Features = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();

  const getFeaturesList = () => {
    if (theme.isNewLook) {
      return (
        <NewLookFeatures>
          {FEATURES_DATA.map((feature) => (
            <NewLookFeature key={feature.title}>
              <NewLookIcon>
                <feature.newLookIcon size={16} strokeWidth={1.75} />
              </NewLookIcon>
              <div>
                <NewLookFeatureTitle>{t(feature.title)}</NewLookFeatureTitle>
                <NewLookFeatureDescription>
                  {t(feature.description)}
                </NewLookFeatureDescription>
              </div>
            </NewLookFeature>
          ))}
        </NewLookFeatures>
      );
    }
    return (
      <FeaturesWrapper>
        {FEATURES_DATA.map((feature) => {
          const Icon = theme.isDarkMode ? feature.darkModeIcon : feature.icon;
          return (
            <FeatureWrapper key={feature.title}>
              <div style={{ position: "relative", overflow: "hidden" }}>
                <IconLine />
                <Icon $size={`${ICON_SIZE}px`} />
              </div>
              <div>
                <Text $weight={500}>{t(feature.title)}</Text>
                <Text type="medium">{t(feature.description)}</Text>
              </div>
            </FeatureWrapper>
          );
        })}
      </FeaturesWrapper>
    );
  };

  const topContainer = (
    <>
      <Title>{t("cancel_subscription.are_you_sure_cancel")}</Title>
      <Description>{t("cancel_subscription.lose_access")}</Description>
    </>
  );

  return (
    <PageLayout
      topContainer={topContainer}
      mainContent={getFeaturesList()}
      Icon={FeaturesMainIcon}
    />
  );
};

export default Features;

const FEATURES_DATA: {
  title: TranslationKeys<LanguageNameSpaces.BILLING>;
  description: TranslationKeys<LanguageNameSpaces.BILLING>;
  icon: (props: any) => React.JSX.Element;
  darkModeIcon: (props: any) => React.JSX.Element;
  newLookIcon: LucideIcon;
}[] = [
  {
    title: "cancel_subscription.features.signals_copy_trading.title",
    description:
      "cancel_subscription.features.signals_copy_trading.description",
    icon: SignalFollowing,
    darkModeIcon: SignalFollowingDark,
    newLookIcon: Radio,
  },
  {
    title: "cancel_subscription.features.versatile_trading_bots.title",
    description:
      "cancel_subscription.features.versatile_trading_bots.description",
    icon: Bots,
    darkModeIcon: BotsDark,
    newLookIcon: Bot,
  },
  {
    title: "cancel_subscription.features.advanced_trading_tools.title",
    description:
      "cancel_subscription.features.advanced_trading_tools.description",
    icon: AdvancedTrading,
    darkModeIcon: AdvancedTradingDark,
    newLookIcon: ChartCandlestick,
  },
  {
    title: "cancel_subscription.features.unified_portfolio.title",
    description: "cancel_subscription.features.unified_portfolio.description",
    icon: PortfolioTracking,
    darkModeIcon: PortfolioTrackingDark,
    newLookIcon: ChartPie,
  },
  {
    title: "cancel_subscription.features.strategy_backtesting.title",
    description:
      "cancel_subscription.features.strategy_backtesting.description",
    icon: Backtesting,
    darkModeIcon: BacktestingDark,
    newLookIcon: History,
  },
];

const ICON_SIZE = 44;

const IconLine = styled.div`
  position: absolute;
  height: ${ICON_SIZE + 6}px;
  width: ${ICON_SIZE + 6}px;
  border-left: 2px solid ${(p) => p.theme.colors.primaryText};
  rotate: -45deg;
  top: -20px;
  left: 14px;
`;
const FeaturesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;
const FeatureWrapper = styled.div`
  padding: 16px;
  border: ${(p) => p.theme.borders.light};
  border-radius: 12px;
  display: flex;
  align-items: center;
  column-gap: 12px;
  width: 370px;
  max-width: 100%;
`;
/** What the plan keeps, as a hairline list (no tinted tiles). */
const NewLookFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const NewLookFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookIcon = styled.span`
  flex: none;
  display: inline-flex;
  margin-top: 2px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookFeatureTitle = styled.div`
  ${newLookType.body};
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookFeatureDescription = styled.div`
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
